import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import i18next from "i18next";
import { logout } from "redux/slices/accountSlice";
import { selectOrginizationDetails } from "redux/slices/orginizationSlice";
import orginizationService from "services/orginization-profile.service";
import "./tenantDeactivationNotice.scss";

const STATUS_CHECK_INTERVAL = 30000;

export default function TenantDeactivationNotice() {
  const dispatch = useDispatch();
  const organization = useSelector(selectOrginizationDetails);
  const [deactivateAt, setDeactivateAt] = useState(null);
  const tenantId = organization?.tenantId;

  useEffect(() => {
    setDeactivateAt(organization?.deactivateAt ?? null);
  }, [organization?.deactivateAt]);

  useEffect(() => {
    if (!tenantId) return undefined;

    let mounted = true;
    const verifyTenantStatus = async () => {
      try {
        const status = await orginizationService.getTenantStatus();
        if (!mounted) return;
        if (!status.isActive) {
          dispatch(logout());
          return;
        }
        setDeactivateAt(status.deactivateAt ?? null);
      } catch (error) {
        if (mounted) {
          console.error("Unable to refresh tenant status:", error);
        }
      }
    };

    verifyTenantStatus();
    const intervalId = window.setInterval(
      verifyTenantStatus,
      STATUS_CHECK_INTERVAL
    );

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, [dispatch, tenantId]);

  useEffect(() => {
    if (!deactivateAt) return undefined;
    let timeoutId;
    const logoutAtDeactivation = () => {
      const remaining = Number(deactivateAt) - Date.now();
      if (remaining <= 0) {
        dispatch(logout());
        return;
      }
      timeoutId = window.setTimeout(
        logoutAtDeactivation,
        Math.min(remaining, 2147483647)
      );
    };

    logoutAtDeactivation();
    return () => window.clearTimeout(timeoutId);
  }, [deactivateAt, dispatch]);

  if (!tenantId || !deactivateAt || Number(deactivateAt) <= Date.now()) {
    return null;
  }

  const date = new Intl.DateTimeFormat(undefined, {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(Number(deactivateAt)));

  return (
    <div className="tenant-deactivation-notice" role="status">
      {i18next.t("labels.tenant_deactivation_notice", { date })}
    </div>
  );
}
